import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import React from 'react';
import styled, { css } from 'styled-components';
import { Arrow } from "../Arrow";
import { Positioner } from "../Popover/Positioner";
import { easeCurves } from "../../motion/eases";
import { theme } from "../../theme";
import { Text } from "../Text";
import { Columns, Column } from "../Columns";
var TRANSITION_DURATION = 200;
export var tooltipFadeAnimation = css(["opacity:0;transform:scale(0.9);&[data-animate-on-mount='false']{opacity:1;transform:scale(1);transition-duration:", "ms;}transition-property:opacity,transform;transition-timing-function:", ";transition-duration:", "ms;&[data-state='entering'],&[data-state='entered']{opacity:1;transform:scale(1);visibility:visible;}&[data-state='exiting']{opacity:0;transform:scale(1);transition-duration:", "ms;}&[data-position='bottom']{transform-origin:50% 0;}&[data-position='bottom-start']{transform-origin:0 0;}&[data-position='bottom-end']{transform-origin:100% 0;}&[data-position='top']{transform-origin:50% 100%;}&[data-position='top-start']{transform-origin:0 100%;}&[data-position='top-end']{transform-origin:100% 100%;}&[data-position='right']{transform-origin:0 50%;}&[data-position='right-start']{transform-origin:0 0;}&[data-position='right-end']{transform-origin:0 100%;}&[data-position='left']{transform-origin:100% 50%;}&[data-position='left-start']{transform-origin:100% 0;}&[data-position='left-end']{transform-origin:100% 100%;}&[data-animate-on-unmount='false']{display:none;}&[data-popper-reference-hidden],&[data-popper-escaped]{display:none;pointer-events:none;}"], TRANSITION_DURATION, easeCurves.primary, TRANSITION_DURATION, TRANSITION_DURATION);
export var tooltipStyles = css(["background:", ";border:1px solid ", ";filter:drop-shadow(0px 2px 4px rgba(0,0,0,0.4));border-radius:", ";padding:", " ", ";text-align:center;max-width:", ";display:inline-block;position:relative;"], theme.colors.palette.grey05, theme.colors.palette.grey20, theme.radii[8], theme.spaces[8], theme.spaces[12], function (p) {
  var _p$$maxWidth;

  return (_p$$maxWidth = p.$maxWidth) !== null && _p$$maxWidth !== void 0 ? _p$$maxWidth : '296px';
});
export var TooltipContainer = styled.div.withConfig({
  displayName: "TooltipContent__TooltipContainer",
  componentId: "vapor__sc-5y4781-0"
})(["", ";", ";"], tooltipStyles, tooltipFadeAnimation);
var defaultOffset = [0, 8];
/**
 * A "dumb" version of the Tooltip which renders a Tooltip without
 * the behavior controls provided by `Tooltip`.
 */

export var TooltipContent = React.forwardRef(function TooltipContent(_ref, ref) {
  var id = _ref.id,
      role = _ref.role,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? defaultOffset : _ref$offset,
      _ref$shouldUsePositio = _ref.shouldUsePositioner,
      shouldUsePositioner = _ref$shouldUsePositio === void 0 ? false : _ref$shouldUsePositio,
      _ref$shouldUsePortal = _ref.shouldUsePortal,
      shouldUsePortal = _ref$shouldUsePortal === void 0 ? false : _ref$shouldUsePortal,
      adornmentBefore = _ref.adornmentBefore,
      adornmentAfter = _ref.adornmentAfter,
      target = _ref.target,
      placement = _ref.placement,
      title = _ref.title,
      details = _ref.details,
      children = _ref.children,
      isShowing = _ref.isShowing,
      _ref$animateOnMount = _ref.animateOnMount,
      animateOnMount = _ref$animateOnMount === void 0 ? true : _ref$animateOnMount,
      _ref$animateOnUnmount = _ref.animateOnUnmount,
      animateOnUnmount = _ref$animateOnUnmount === void 0 ? true : _ref$animateOnUnmount,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? theme.zIndices.tooltip : _ref$zIndex,
      _ref$maxWidth = _ref.maxWidth,
      maxWidth = _ref$maxWidth === void 0 ? '296px' : _ref$maxWidth,
      other = _objectWithoutProperties(_ref, ["id", "role", "offset", "shouldUsePositioner", "shouldUsePortal", "adornmentBefore", "adornmentAfter", "target", "placement", "title", "details", "children", "isShowing", "animateOnMount", "animateOnUnmount", "zIndex", "maxWidth"]);

  return /*#__PURE__*/React.createElement(Positioner, {
    isShowing: isShowing,
    target: target,
    transitionDuration: {
      enter: animateOnMount ? TRANSITION_DURATION : 0,
      exit: TRANSITION_DURATION
    },
    placement: placement,
    offset: offset,
    shouldUsePositioner: shouldUsePositioner,
    shouldUsePortal: shouldUsePortal,
    zIndex: zIndex
  }, function (_ref2) {
    var state = _ref2.state,
        position = _ref2.position;
    return /*#__PURE__*/React.createElement(TooltipContainer, _extends({
      id: id,
      role: role,
      ref: ref,
      "data-position": position,
      "data-state": state,
      "data-animate-on-mount": animateOnMount,
      "data-animate-on-unmount": isShowing || animateOnUnmount,
      "data-testid": "tooltip-container",
      $maxWidth: maxWidth
    }, other), children || /*#__PURE__*/React.createElement(Columns, {
      gap: 12,
      alignY: "center"
    }, adornmentBefore && /*#__PURE__*/React.createElement(Column, {
      width: "content"
    }, adornmentBefore), /*#__PURE__*/React.createElement(Column, null, typeof title === 'string' ? /*#__PURE__*/React.createElement(TooltipTitle, null, title) : title, details && /*#__PURE__*/React.createElement(TooltipDetails, null, details)), adornmentAfter && /*#__PURE__*/React.createElement(Column, {
      width: "content"
    }, adornmentAfter)), /*#__PURE__*/React.createElement(Arrow, {
      position: position
    }));
  });
});
export var TooltipTitle = styled(Text).attrs({
  color: 'primary',
  variant: 'secondary'
}).withConfig({
  displayName: "TooltipContent__TooltipTitle",
  componentId: "vapor__sc-5y4781-1"
})(["overflow-wrap:break-word;"]);
export var TooltipDetails = styled(Text).attrs({
  color: 'secondary',
  variant: 'secondary'
}).withConfig({
  displayName: "TooltipContent__TooltipDetails",
  componentId: "vapor__sc-5y4781-2"
})(["overflow-wrap:break-word;"]);